import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter, useMenu } from 'common/utils/hooks';
import { useLocation } from 'react-router-dom';
import { getHelpSupportUrl } from 'common/utils/helpSupportUrl';
import { zIndex, shadows } from 'common/styles/mixins';
import logo from 'assets/common/logo-extendeal.svg';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import MenuIcon from '@material-ui/icons/Menu';
import { MobileDrawer } from './mobile-drawer';
import { Menu } from 'components';
import { MenuProps } from './menu';
import {
  userNameSelector,
  userRoleNameFromApiSelector,
  roleIdentifierSelector,
  userIdSelector,
  currentUserSelector
} from 'common/redux/global/selectors';
import * as GlobalActions from 'common/redux/global/actions';
import { AccountCircleOutline, HelpOutline, KeyOutline, Logout } from 'assets/icons';
import {
  applyTheme,
  getDocumentTheme,
  getUserTheme,
  normalizeThemeName
} from 'common/utils/helpers';

function humanizeRoleId(id: string) {
  if (!id) {
    return '';
  }
  return id
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function useUserRoleLabel() {
  const { t } = useTranslation();
  const apiName = useSelector(userRoleNameFromApiSelector);
  const identifier = useSelector(roleIdentifierSelector);
  const trimmed = apiName != null ? String(apiName).trim() : '';
  if (trimmed) {
    return trimmed;
  }
  if (!identifier) {
    return '';
  }
  const key = `shared:user_roles.${identifier}`;
  const translated = t(key);
  if (translated !== key) {
    return translated;
  }
  return humanizeRoleId(identifier);
}

declare var process: {
  env: {
    [key: string]: string;
  };
};

export interface UserPanelAction {
  label: string;
  onClick(): void;
  icon?: React.ReactNode;
}

export interface UserPanelMenuProps extends Omit<MenuProps, 'children'> {
  actions: Array<UserPanelAction>;
}

export interface HeaderIconProps {
  color: string;
  SvgComponent: React.FC<React.SVGProps<SVGSVGElement>>;
  width: number;
  height: number;
}

const MENU_ICON_COLOR = '#AEAEAF';
const HEADER_ICON_COLOR = '#ffffff';
const NEW_BULLET_COLOR = '#FF6300';

const useStyles = makeStyles(({ palette }) => ({
  appBar: {
    backgroundColor: palette.B.A100,
    zIndex: zIndex.sidebar + 1,
    height: '50px',
    boxShadow: shadows[1]
  },
  userRole: {
    marginTop: '4px',
    fontSize: '16px',
    lineHeight: '22px',
    color: 'white'
  },
  toolbar: {
    paddingLeft: '16px',
    height: '50px',
    minHeight: '50px',
    paddingRight: 0
  },
  toolbarButtonsContainer: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    marginRight: '10px',
    gap: '5px'
  },
  headerIconButton: {
    color: HEADER_ICON_COLOR,
    opacity: 1,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)'
    },
    '& svg': {
      fill: HEADER_ICON_COLOR,
      color: HEADER_ICON_COLOR,
      opacity: 1
    }
  },
  userIconWrap: {
    position: 'relative',
    display: 'inline-flex',
    lineHeight: 0,
    '&::after': {
      // SCI `.has-new-bullet` — bullet naranja arriba-derecha del ícono de usuario
      content: '""',
      position: 'absolute',
      top: 2,
      right: -2,
      width: 9,
      height: 9,
      borderRadius: '50%',
      backgroundColor: NEW_BULLET_COLOR,
      zIndex: 2,
      pointerEvents: 'none'
    }
  },
  logo: {
    width: '114px',
    height: '19px',
    cursor: 'pointer'
  },
  userMenuHeader: {
    padding: '8px 16px',
    pointerEvents: 'none' as const
  },
  userMenuName: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '20px',
    color: palette.text.primary
  },
  userMenuRole: {
    marginTop: 2,
    fontSize: 13,
    lineHeight: '18px',
    fontWeight: 400,
    color: palette.text.secondary
  },
  themePicker: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: '4px 16px 8px',
    boxSizing: 'border-box' as const
  },
  themePickerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '0 0 5px',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
    color: '#666666',
    textAlign: 'left' as const
  },
  themePickerNew: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: '2px 7px',
    backgroundColor: '#7c6bea',
    color: '#fff',
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase' as const,
    lineHeight: 1.2,
    borderRadius: 999,
    pointerEvents: 'none' as const,
    userSelect: 'none' as const
  },
  themeOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    margin: 0,
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    color: '#666666',
    fontSize: 14,
    lineHeight: '32px',
    textAlign: 'left' as const,
    fontFamily: 'inherit'
  },
  themeRadio: {
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    width: 14,
    height: 14,
    margin: 0,
    flexShrink: 0,
    border: '1.5px solid rgba(11, 32, 51, 0.28)',
    borderRadius: '50%',
    background: '#fff',
    cursor: 'pointer',
    '&:checked': {
      borderColor: 'rgba(11, 32, 51, 0.45)',
      background: 'radial-gradient(circle, rgba(11, 32, 51, 0.55) 0 38%, #fff 40%)'
    }
  },
  themeLabel: {
    flex: 1,
    color: '#666666',
    fontWeight: 400,
    lineHeight: '30px'
  },
  menuItemIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
    color: MENU_ICON_COLOR,
    fill: MENU_ICON_COLOR,
    opacity: 1,
    display: 'block'
  },
  menuItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }
}));

function MobileMenu() {
  const isMobile = useMediaQuery('(max-width:640px)');
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = () => setOpen(prev => !prev);

  if (!isMobile) {
    return null;
  }

  return (
    <>
      <IconButton color="inherit" aria-label="open drawer" onClick={toggleDrawer} edge="start">
        <MenuIcon />
      </IconButton>
      <MobileDrawer {...{ open }} onOpen={toggleDrawer} onClose={toggleDrawer} />
    </>
  );
}

function ThemePicker({
  value,
  onChange
}: {
  value: 'light' | 'dark';
  onChange: (theme: 'light' | 'dark') => void;
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  const select = (theme: 'light' | 'dark') => (e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation();
    if (theme === value) {
      return;
    }
    onChange(theme);
  };

  return (
    <div className={classes.themePicker} onClick={e => e.stopPropagation()}>
      <p className={classes.themePickerTitle}>
        <span>{t('shared:theme_mode')}</span>
        <span className={classes.themePickerNew}>{t('shared:badge_new')}</span>
      </p>
      <label className={classes.themeOption}>
        <input
          className={classes.themeRadio}
          type="radio"
          name="app-theme"
          value="light"
          checked={value === 'light'}
          onChange={select('light')}
          onClick={select('light')}
        />
        <span className={classes.themeLabel}>{t('shared:theme_light')}</span>
      </label>
      <label className={classes.themeOption}>
        <input
          className={classes.themeRadio}
          type="radio"
          name="app-theme"
          value="dark"
          checked={value === 'dark'}
          onChange={select('dark')}
          onClick={select('dark')}
        />
        <span className={classes.themeLabel}>{t('shared:theme_dark')}</span>
      </label>
    </div>
  );
}

function UserPanelMenu({ actions, ...props }: UserPanelMenuProps) {
  const classes = useStyles();
  const userName = useSelector(userNameSelector);
  const roleLabel = useUserRoleLabel();
  const userId = useSelector(userIdSelector);
  const currentUser = useSelector(currentUserSelector);
  const [theme, setTheme] = React.useState<'light' | 'dark'>(() => {
    return getDocumentTheme() || getUserTheme(currentUser) || 'light';
  });

  React.useEffect(() => {
    if (!props.open) {
      return;
    }
    const next = getDocumentTheme() || getUserTheme(currentUser) || 'light';
    setTheme(next);
  }, [props.open, currentUser]);

  const onSelectTheme = (next: 'light' | 'dark') => {
    const normalized = normalizeThemeName(next);
    if (!normalized || normalized === theme) {
      return;
    }
    setTheme(normalized);
    applyTheme(normalized, { persist: true, userId, manual: true });
  };

  return (
    <Menu {...props}>
      <div className={classes.userMenuHeader}>
        <div className={classes.userMenuName}>{userName}</div>
        {roleLabel ? <div className={classes.userMenuRole}>{roleLabel}</div> : null}
      </div>
      <Menu.Divider />
      <ThemePicker value={theme} onChange={onSelectTheme} />
      <Menu.Divider />
      {actions.map(action => (
        <Menu.Item key={action.label} onClick={action.onClick}>
          <span className={classes.menuItemContent}>
            {action.icon}
            {action.label}
          </span>
        </Menu.Item>
      ))}
    </Menu>
  );
}

function HeaderIcon({ SvgComponent, color, width, height }: HeaderIconProps) {
  return <SvgComponent fill={color} {...{ width, height }} />;
}

export function Header() {
  const classes = useStyles();
  const { push } = useRouter();
  const { pathname } = useLocation();
  const profileMenu = useMenu();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const helpHref = getHelpSupportUrl(pathname);

  const handleLogoClick = () => push('/');

  const onOpenChangePasswordModal = () => {
    dispatch(GlobalActions.toggleChangePasswordModal());
    profileMenu.handleClose();
  };

  const signOut = () => dispatch(GlobalActions.userSignout());

  return (
    <AppBar className={classes.appBar} position="absolute">
      <Toolbar className={classes.toolbar}>
        <MobileMenu />
        <img className={classes.logo} src={logo} alt="" onClick={handleLogoClick} />
        <div className={classes.toolbarButtonsContainer}>
          <IconButton
            className={classes.headerIconButton}
            target="_blank"
            rel="noopener noreferrer"
            href={helpHref || '#'}
            color="inherit"
          >
            <HeaderIcon
              color={HEADER_ICON_COLOR}
              SvgComponent={HelpOutline}
              width={28}
              height={28}
            />
          </IconButton>
          <IconButton
            className={classes.headerIconButton}
            title={t('shared:user_panel')}
            onClick={profileMenu.handleMenu}
          >
            <span className={classes.userIconWrap}>
              <HeaderIcon
                color={HEADER_ICON_COLOR}
                SvgComponent={AccountCircleOutline}
                width={28}
                height={28}
              />
            </span>
          </IconButton>
        </div>
        <UserPanelMenu
          anchorEl={profileMenu.anchorEl}
          open={profileMenu.open}
          onClose={profileMenu.handleClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right'
          }}
          getContentAnchorEl={null}
          style={{ top: 15 }}
          actions={[
            {
              label: t('shared:change_password'),
              onClick: onOpenChangePasswordModal,
              icon: (
                <KeyOutline
                  className={classes.menuItemIcon}
                  width={16}
                  height={16}
                  fill={MENU_ICON_COLOR}
                />
              )
            },
            {
              label: t('shared:actions.exit'),
              onClick: signOut,
              icon: (
                <Logout
                  className={classes.menuItemIcon}
                  width={16}
                  height={16}
                  fill={MENU_ICON_COLOR}
                />
              )
            }
          ]}
        />
      </Toolbar>
    </AppBar>
  );
}
